import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from './Header';
import { getTranslation, Language } from '../translations';
import { getDeviceTypeById } from '../config/deviceConfig';
import { useDeviceModels } from '../hooks/useDeviceModels';
import { ArrowLeft, Tag } from 'lucide-react';
import CustomDeviceRequestModal from './CustomDeviceRequestModal';
import { formatDeviceNameMeta, splitDeviceDisplayName } from '../utils/deviceDisplayName';

const getDeviceIcon = (deviceType: string) => {
  const type = deviceType.toLowerCase();
  switch (true) {
    case type === 'iphone':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
        </svg>
      );
    case type === 'ipad':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <rect x="4" y="3" width="16" height="18" rx="2" />
          <circle cx="12" cy="17" r="1" />
        </svg>
      );
    case type === 'macbook':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M2 4a2 2 0 012-2h16a2 2 0 012 2v11a2 2 0 01-2 2H4a2 2 0 01-2-2V4zm0 14h20v1a1 1 0 01-1 1H3a1 1 0 01-1-1v-1z" />
        </svg>
      );
    case type === 'mac' || type === 'imac':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" />
        </svg>
      );
    case type === 'apple watch' || type === 'apple-watch':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 5.5c.6-1.4 1-2.2 1-3.1C10 1.6 10.7 1 12 1s2 .6 2 1.4c0 .9.4 1.7 1 3.1" />
          <rect x="6" y="7" width="12" height="10" rx="3.2" />
          <rect x="18.2" y="10" width="2" height="4" rx="1" />
          <path d="M10.2 12.8l3.2-3.2M11.7 14.7l2.1-2.1" />
          <path d="M9 18.5c.6 1.4 1 2.2 1 3.1 0 .8.7 1.4 2 1.4s2-.6 2-1.4c0-.9.4-1.7 1-3.1" />
        </svg>
      );
    case type === 'samsung':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
        </svg>
      );
    default:
      return (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
        </svg>
      );
  }
};

const pulseGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(99, 102, 241, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const macFadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const macFadeOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to   { opacity: 0; transform: translateY(-12px); }
`;

const MacTransitionWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$phase',
})<{ $phase: 'in' | 'out' | 'idle' }>`
  animation: ${props => props.$phase === 'in' ? macFadeIn : props.$phase === 'out' ? macFadeOut : 'none'} 250ms cubic-bezier(0.4, 0, 0.2, 1);
  animation-fill-mode: both;
  pointer-events: ${props => props.$phase === 'out' ? 'none' : 'auto'};
`;


const Container = styled.div`
  background: linear-gradient(to bottom, #fafafb 0%, #f8f9fa 100%);
  min-height: 100vh;
  padding: clamp(7rem, 8vw, 9rem) 1.5rem 6rem;
  position: relative;
  overflow-x: hidden;
  overflow-y: visible;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 200px;
    background: radial-gradient(ellipse at top, rgba(0, 153, 255, 0.05) 0%, transparent 70%);
    pointer-events: none;
  }

  .dark & {
    background: linear-gradient(to bottom, #0a0c10 0%, #121418 100%);
  }

  .dark &::before {
    background: radial-gradient(ellipse at top, rgba(148, 163, 184, 0.06) 0%, transparent 70%);
  }

  @media (max-width: 768px) {
    padding: 6.5rem 1.5rem 3rem;
    overflow-x: hidden;
    overflow-y: visible;
    min-height: auto;
  }

  @media (max-width: 480px) {
    padding: 5.5rem 1.5rem 2.5rem;
    overflow-x: hidden;
    overflow-y: visible;
    min-height: auto;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
  overflow: visible;

  @media (max-width: 480px) {
    overflow: visible;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
`;

const HeaderIconContainer = styled.div`
  display: inline-flex;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
  border: 1px solid #e5e7eb;

  svg {
    width: 2rem;
    height: 2rem;
    color: #1f2937;
  }

  .dark & {
    background: rgba(17, 24, 39, 0.6);
    border: 1px solid #334155;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);

    svg { color: #e2e8f0; }
  }
  @media (max-width: 768px) {
    padding: 0.5rem;
    margin-bottom: 0.75rem;
    margin-top: 0;
    svg { width: 1.5rem; height: 1.5rem; }
  }
`;

const Title = styled.h1`
  font-size: 3.25rem;
  font-weight: 700;
  letter-spacing: -0.045em;
  margin-bottom: 0.9rem;
  line-height: 1.2;
  padding-bottom: 0.1em;
  background: linear-gradient(180deg, #0f172a 0%, #1f2937 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;

  @media (min-width: 1025px) {
    font-size: 3.5rem;
    margin-bottom: 1rem;
  }
  @media (max-width: 768px) {
    font-size: 2.4rem;
  }
  @media (max-width: 480px) {
    font-size: 1.6rem;
    line-height: 1.2;
    margin-bottom: 0.5rem;
  }

  .dark & {
    background: linear-gradient(180deg, #ffffff 0%, #cbd5e1 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: #667085;
  max-width: 44rem;
  margin: 0 auto;
  line-height: 1.65;
  font-weight: 400;
  letter-spacing: -0.01em;

  @media (min-width: 1025px) {
    font-size: 1.15rem;
    max-width: 46rem;
  }
  @media (max-width: 768px) {
    font-size: 1rem;
    max-width: 36rem;
  }
  @media (max-width: 480px) {
    font-size: 0.875rem;
    line-height: 1.4;
    max-width: 100%;
    padding: 0 0.5rem;
  }

  .dark & {
    color: #cbd5e1;
  }
`;

// Search input with dark mode
const SearchWrapper = styled.div`
  position: relative;
  max-width: 400px;
  margin: 0 auto;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.9rem 5rem 0.9rem 1rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 14px;
  font-size: 0.95rem;
  outline: none;
  transition: all 0.2s ease;
  background-color: white;
  color: #0f172a;
  box-shadow: inset 0 1px 2px rgba(16,24,40,0.04);

  &::placeholder { color: #94a3b8; }

  &:focus {
    border-color: #94a3b8;
    box-shadow: 0 0 0 4px rgba(148, 163, 184, 0.15);
  }

  .dark & {
    background-color: #0b0d13;
    border-color: #334155;
    color: #e2e8f0;
    box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.35);
  }

  .dark &:focus {
    border-color: #64748b;
    box-shadow: 0 0 0 4px rgba(100, 116, 139, 0.2);
  }
`;

const SearchRight = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SearchCount = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  color: #94a3b8;
  background: #f1f5f9;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  letter-spacing: 0.01em;
  white-space: nowrap;

  .dark & {
    color: #64748b;
    background: #1e293b;
  }
`;

const SearchIcon = styled.div`
  color: #9ca3af;
  display: flex;
  align-items: center;

  .dark & { color: #64748b; }

  button {
    background: none;
    border: none;
    color: inherit;
    cursor: pointer;
    padding: 2px;
    display: flex;
    align-items: center;
  }
`;

const FilterContainer = styled.div`
  position: sticky;
  top: 5.5rem;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(24px);
  padding: 1.75rem 2rem;
  border-radius: 20px;
  border: 1px solid rgba(226, 232, 240, 0.7);
  box-shadow:
    0 20px 40px rgba(2, 6, 23, 0.06),
    0 1px 2px rgba(2, 6, 23, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  margin-bottom: 3rem;
  z-index: 10;

  @media (min-width: 1025px) {
    top: 7.5rem;
    padding: 2rem 2.25rem;
    border-radius: 22px;
  }

  @media (max-width: 768px) {
    top: 6.5rem;
    padding: 1rem 1.125rem;
    border-radius: 14px;
    margin-bottom: 1.5rem;
    position: relative;
    top: auto;
    z-index: 5;
  }

  .dark & {
    background: rgba(15, 17, 23, 0.8);
    border: 1px solid #2e3441;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255,255,255,0.05);
  }
`;

const FilterGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.625rem;
  flex-wrap: wrap;
  max-width: 420px;
  margin: 0 auto;
  @media (max-width: 480px) {
    max-width: 320px;
    gap: 0.5rem;
  }
`;

const FilterPill = styled.button<{ $isActive: boolean }>`
  background: ${props => props.$isActive ? '#0f172a' : 'white'};
  color: ${props => props.$isActive ? 'white' : '#0f172a'};
  border: 1px solid ${props => props.$isActive ? '#0f172a' : '#e2e8f0'};
  border-radius: 9999px;
  padding: 0.7rem 1.35rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg { width: 1.2rem; height: 1.2rem; }

  &:hover { background: ${props => props.$isActive ? '#111827' : '#f8fafc'}; }

  .dark & {
    background: ${props => props.$isActive ? '#1f2937' : 'transparent'};
    color: ${props => props.$isActive ? '#e5e7eb' : '#e5e7eb'};
    border: 1px solid ${props => props.$isActive ? '#3a4250' : '#2e3441'};
  }

  .dark &:hover {
    background: ${props => props.$isActive ? '#1f2937' : 'rgba(148,163,184,0.06)'};
  }
  @media (max-width: 480px) {
    padding: 0.45rem 0.9rem;
    font-size: 0.8rem;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  color: #64748b;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #0f172a;
    transform: translateX(-2px);
  }

  .dark & {
    background: #0f1216;
    border-color: #334155;
    color: #94a3b8;

    &:hover {
      background: #1e293b;
      border-color: #475569;
      color: #e2e8f0;
    }
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ModelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  width: 100%;
  overflow: visible;

  @media (max-width: 1280px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem 0.75rem;
    align-items: stretch;
    width: 100%;
    overflow: visible;
  }

  @media (max-width: 480px) {
    gap: 0.625rem;
  }
`;

const ModelCard = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
  border-radius: 24px;
  padding: 1.75rem 1.25rem;
  text-align: center;
  border: 1px solid #e2e8f0;
  transition: box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  cursor: pointer;
  min-height: 100%;
  box-sizing: border-box;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.01), 0 2px 4px -1px rgba(0, 0, 0, 0.01);

  &:hover {
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 10px 10px -5px rgba(0, 0, 0, 0.03);
    border-color: #94a3b8;
  }

  .dark & {
    background: #0f1216;
    border: 1px solid #2e3441;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2);
  }

  .dark &:hover {
    border-color: #64748b;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.4);
    background: #1a1d26;
  }

  @media (max-width: 640px) {
    border-radius: 18px;
    padding: 1rem 0.75rem;
  }
`;

const CardHeader = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
  padding: 0.5rem 0;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 180px;

  @media (min-width: 1025px) {
    height: 200px;
    margin-bottom: 1.75rem;
  }
  @media (max-width: 640px) {
    height: 120px;
    margin-bottom: 0.75rem;
    padding: 0;
  }
`;

const ModelImage = styled.img`
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
  transition: transform 0.3s ease;
  filter: drop-shadow(0 10px 15px rgba(0,0,0,0.05));

  ${ModelCard}:hover & {
    transform: scale(1.08);
    filter: drop-shadow(0 20px 25px rgba(0,0,0,0.1));
  }
`;

const CardContent = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1.5rem;

  @media (max-width: 640px) {
    margin-bottom: 0.75rem;
  }
`;

const ModelTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  line-height: 1.4;
  text-align: center;
  text-wrap: balance;
  word-break: normal;
  overflow-wrap: break-word;
  hyphens: none;
  /* Reserveer ruimte voor 1 titel-regel + 1 subtitel-regel, zodat kaarten
     consistent even hoog zijn — ook voor types zonder subtitel (iPhone/Samsung). */
  min-height: 3em;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .dark & { color: #e2e8f0; }

  @media (min-width: 1025px) {
    font-size: 1.35rem;
    min-height: 3.2em;
  }
  @media (max-width: 640px) {
    font-size: 0.95rem;
    line-height: 1.3;
    min-height: 2.8em;
  }

  .model-meta {
    display: block;
    font-size: 0.85em;
    font-weight: 500;
    color: #64748b;
    margin-top: 0.35rem;
  }

  .dark & .model-meta { color: #94a3b8; }
`;

const PriceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  margin-top: auto;
`;

const Price = styled.span`
  font-size: 1.5rem;
  font-weight: 800;
  color: #22c55e;
  letter-spacing: -0.02em;

  .dark & {
    color: #10b981;
  }

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

const PriceSubtitle = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.05em;
  text-transform: uppercase;

  .dark & {
    color: #9ca3af;
  }
`;

const CTAButton = styled.button`
  width: 100%;
  background: #1f2937;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 0.85rem 1.25rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  @media (min-width: 1025px) {
    padding: 0.95rem 1.5rem;
    font-size: 0.95rem;
  }

  .arrow-icon {
    width: 17px;
    height: 17px;
    stroke: currentColor;
    stroke-width: 2.5;
    transition: transform 0.2s ease;
  }

  &:hover {
    background: #374151;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);

    .arrow-icon {
      transform: translateX(3px);
    }
  }

  &:active {
    transform: translateY(0);
  }

  .dark & {
    background: #334155;
  }

  .dark &:hover {
    background: #475569;
  }

  @media (max-width: 640px) {
    padding: 0.7rem 0.875rem;
    font-size: 0.8rem;
    border-radius: 10px;
    gap: 0.5rem;

    .arrow-icon {
      width: 14px;
      height: 14px;
    }
  }
`;

const RequestIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border: 1.5px solid #cbd5e1;
  border-radius: 20px;
  margin-bottom: 1.25rem;
  background: #f8fafc;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  svg {
    width: 1.5rem;
    height: 1.5rem;
    color: #9ca3af;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .dark & {
    border-color: #475569;
    background: #1e293b;
    svg { color: #94a3b8; }
  }

  @media (max-width: 480px) {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    margin-bottom: 1rem;
    svg { width: 1.25rem; height: 1.25rem; }
  }
`;

const RequestTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.5rem;

  .dark & { color: #e2e8f0; }
`;

const RequestDescription = styled.p`
  font-size: 0.9rem;
  color: #64748b;
  margin-bottom: 1.5rem;
  line-height: 1.5;

  .dark & { color: #94a3b8; }
`;

const RequestCTAButton = styled.button`
  background: #0f172a;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 0.85rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: auto;
  width: 100%;

  &:hover {
    background: #1e293b;
    transform: translateY(-1px);
  }

  .dark & {
    background: #334155;
    &:hover { background: #475569; }
  }

  
`;

const RequestCard = styled.div<{ $delay?: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  background: transparent;
  border-radius: 24px;
  padding: 2rem 1.5rem;
  text-align: center;
  border: 2px dashed #cbd5e1;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: 100%;

  &:hover {
    background: #f8fafc;
    border-color: #64748b;
    transform: translateY(-4px);
  }

  .dark & {
    border-color: #475569;
  }

  .dark &:hover {
    background: #0b0d13;
  }
  @media (max-width: 480px) {
    padding: 1.25rem 1rem;
    border-radius: 18px;
  }
`;

const SkeletonCard = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
  border-radius: 20px;
  padding: 1.25rem 1rem;
  border: 1px solid #e6edf5;
  box-shadow: 0 10px 24px rgba(2,6,23,0.06);
  min-height: 100%;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;

  /* Desktop refinement */
  @media (min-width: 1025px) {
    border-radius: 24px;
    padding: 2rem 1.5rem;
    box-shadow: 0 18px 36px rgba(2,6,23,0.1);
    border: 1px solid #e7eef8;
  }

  .dark & {
    background: #0f1216;
    border: 1px solid #262b36;
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5);
  }

  @media (max-width: 480px) {
    border-radius: 20px;
    padding: 1.125rem 1rem;
    min-height: auto;
  }
`;

const SkeletonBase = styled.div`
  background: #e2e8f0;
  background-image: linear-gradient(to right, #e2e8f0 0%, #f1f5f9 20%, #e2e8f0 40%, #e2e8f0 100%);
  background-repeat: no-repeat;
  background-size: 2000px 100%;
  animation: ${shimmer} 1.5s infinite linear;
  border-radius: 6px;

  .dark & {
    background: #1e293b;
    background-image: linear-gradient(to right, #1e293b 0%, #334155 20%, #1e293b 40%, #1e293b 100%);
  }
`;

const SkeletonHeader = styled.div`
  position: relative;
  margin-bottom: 1rem;
  padding: 1rem 0;
  display: flex;
  justify-content: center;

  @media (min-width: 1025px) {
    margin-bottom: 1.25rem;
    padding: 1.25rem 0 1rem;
  }
  @media (max-width: 480px) {
    margin-bottom: 1rem;
    padding: 0.875rem 0 0.625rem;
    min-height: 115px;
  }
`;

const SkeletonImage = styled(SkeletonBase)`
  width: 120px;
  height: 120px;
  border-radius: 12px;

  @media (min-width: 1025px) {
    width: 160px;
    height: 160px;
  }
  @media (max-width: 480px) {
    width: 90px;
    height: 90px;
  }
`;

const SkeletonContent = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 0 0.5rem;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const SkeletonTitle = styled(SkeletonBase)`
  width: 80%;
  height: 1.5rem;
  margin: 0 auto;
`;

const SkeletonSubtitle = styled(SkeletonBase)`
  width: 50%;
  height: 1rem;
  margin: 0 auto;
`;

const SkeletonButton = styled(SkeletonBase)`
  width: 100%;
  height: 2.5rem;
  border-radius: 12px;
  margin-top: auto;

  @media (min-width: 1025px) {
    height: 3rem;
  }
`;

const LoadMoreWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 2.5rem 0 1rem;
`;

const LoadMoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: #0f172a;
  color: #ffffff;
  border: none;
  border-radius: 99px;
  padding: 0.875rem 2.5rem;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);

  &:hover:not(:disabled) {
    background: #1e293b;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.2);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .dark & {
    background: #1f2937;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);

    &:hover:not(:disabled) {
      background: #374151;
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.5);
    }
  }

  @media (max-width: 480px) {
    padding: 0.75rem 2rem;
    font-size: 0.875rem;
  }
`;

const SpinnerInline = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
`;

const ErrorIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #fef2f2;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;

  svg {
    width: 32px;
    height: 32px;
    color: #ef4444;
  }

  .dark & {
    background: rgba(239, 68, 68, 0.1);
  }
`;

const ErrorTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.5rem;

  .dark & {
    color: #f1f5f9;
  }
`;

const ErrorMessage = styled.p`
  font-size: 0.9375rem;
  color: #64748b;
  max-width: 400px;
  line-height: 1.5;

  .dark & {
    color: #94a3b8;
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  grid-column: 1 / -1;
`;

const EmptyStateIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 96px;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  border-radius: 50%;
  margin-bottom: 1.5rem;

  svg {
    width: 48px;
    height: 48px;
    color: #94a3b8;
    stroke-width: 1.5;
  }

  .dark & {
    background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
    svg { color: #64748b; }
  }
`;

const EmptyStateTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;

  .dark & { color: #f1f5f9; }
`;

const EmptyStateText = styled.p`
  font-size: 0.95rem;
  color: #64748b;
  max-width: 320px;
  line-height: 1.6;
  margin-bottom: 1.5rem;

  .dark & { color: #94a3b8; }
`;


const PAGE_SIZE = 40;

const MAC_CATEGORIES = [
  { id: 'iMac', label: 'iMac', match: (k: string) => k.startsWith('imac') },
  { id: 'Mac Pro', label: 'Mac Pro', match: (k: string) => k.startsWith('mac-pro') },
  { id: 'Mac Studio/Mini', label: 'Studio / Mini', match: (k: string) => k.startsWith('mac-studio') || k.startsWith('mac-mini') },
];

const DeviceModelList: React.FC<{ deviceType: string, onSelect: (modelId: string, modelName?: string) => void, onBack?: () => void, macGroupFilter?: string | null, onMacGroupSelect?: (group: string) => void }> = ({ deviceType, onSelect, onBack, macGroupFilter, onMacGroupSelect }) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [transitionPhase, setTransitionPhase] = useState<'in' | 'out' | 'idle'>('idle');
  const pendingGroupRef = useRef<string | null>(null);

  const [isCustomRequestModalOpen, setIsCustomRequestModalOpen] = useState(false);
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(language as Language, key);

  // Debounce search input (350 ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset local UI state when device type changes
  useEffect(() => {
    setActiveFilter('all');
    setSearchInput('');
    setDebouncedSearch('');
  }, [deviceType]);

  // Reset search and filter when macGroupFilter changes
  useEffect(() => {
    setSearchInput('');
    setActiveFilter('all');
  }, [macGroupFilter]);

  // Animated category transition: fade out → change group → fade in
  const handleCategoryClick = useCallback((group: string) => {
    if (!onMacGroupSelect) return;
    pendingGroupRef.current = group;
    setTransitionPhase('out');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      onMacGroupSelect(group);
      setTransitionPhase('in');
      setTimeout(() => setTransitionPhase('idle'), 260);
    }, 250);
  }, [onMacGroupSelect]);

  // Animated back from models → categories
  const handleAnimatedBack = useCallback(() => {
    if (!onBack) return;
    setTransitionPhase('out');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      onBack();
      setTransitionPhase('in');
      setTimeout(() => setTransitionPhase('idle'), 260);
    }, 250);
  }, [onBack]);

  // Fetch models with pagination + server-side search
  const { models, isLoading, isLoadingMore, hasMore, totalCount, error, loadMore } = useDeviceModels(deviceType, language, debouncedSearch);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 480);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchInput('');
    setDebouncedSearch('');
  }, []);

  const deviceTypeConfig = getDeviceTypeById(deviceType);
  // Normaliseer underscores/spaces → hyphens zodat DB-keys zoals "apple_watch" matchen.
  const normalizedDeviceType = deviceType.toLowerCase().replace(/[_\s]+/g, '-');
  // Format device type name: use static config name, or format common keys properly.
  const formatDeviceTypeName = (): string => {
    const map: Record<string, string> = {
      iphone: 'iPhone', samsung: 'Samsung', ipad: 'iPad',
      macbook: 'MacBook', 'apple-watch': 'Apple Watch',
      imac: 'Mac', mac: 'Mac',
    };
    return map[normalizedDeviceType] || deviceType;
  };
  const deviceTypeName = deviceTypeConfig?.name || formatDeviceTypeName();

  // ── Variant-based filter pills ──
  // Als filter_tags aanwezig zijn, zijn die leidend (DB is source of truth).
  // Anders val terug op key-substring heuristiek (voor legacy data zonder tags).
  // Voorkomt bv. dat "ipad-mini-a17-pro" zowel onder Mini als Pro valt.
  const hasTags = (t?: string[]) => Array.isArray(t) && t.length > 0;
  // 'mainline' is een intern segmentlabel uit de importscripts: het scheidt de
  // gewone lijn van SE, Ultra, Air, Pro en Mini. Alleen de Apple Watch en de
  // iPad dragen die tag. De klant kent die lijn onder een andere naam: bij het
  // horloge is dat Series, bij de iPad is het simpelweg het standaardmodel.
  const mainlineLabel = normalizedDeviceType === 'apple-watch'
    ? 'Series'
    : t('standardModels') || 'Standaard';
  const VARIANT_FILTERS: { id: string; label: string; match: (key: string, tags?: string[]) => boolean }[] = [
    { id: 'all',      label: t('allModels') || 'Alle modellen', match: () => true },
    { id: 'ultra',    label: 'Ultra',    match: (k, t) => hasTags(t) ? t!.includes('ultra') : k.includes('-ultra') },
    { id: 'promax',   label: 'Pro Max',  match: (k, t) => hasTags(t) ? (t!.includes('pro-max') || t!.includes('promax')) : k.includes('-pro-max') },
    { id: 'pro',      label: 'Pro',      match: (k, t) => hasTags(t) ? t!.includes('pro') : (k.includes('-pro') && !k.includes('-pro-max')) },
    { id: 'plus',     label: 'Plus',     match: (k, t) => hasTags(t) ? t!.includes('plus') : k.includes('-plus') },
    { id: 'edge',     label: 'Edge',     match: (k, t) => hasTags(t) ? t!.includes('edge') : k.includes('-edge') },
    { id: 'air',      label: 'Air',      match: (k, t) => hasTags(t) ? t!.includes('air') : k.includes('-air') },
    { id: 'mini',     label: 'Mini',     match: (k, t) => hasTags(t) ? t!.includes('mini') : k.includes('-mini') },
    { id: 'fe',       label: 'FE',       match: (k, t) => hasTags(t) ? t!.includes('fe') : k.includes('-fe') },
    { id: 'se',       label: 'SE',       match: (k, t) => hasTags(t) ? t!.includes('se') : /(^|-)se(-|$)/.test(k) },
    { id: 'mainline', label: mainlineLabel, match: (_k, t) => !!t?.includes('mainline') },
  ];

  const isMacPage = deviceTypeName === 'Mac' || deviceType.toLowerCase() === 'imac';

  // Only show pills that have at least one matching model
  const filters = useMemo(() => {
    if (isMacPage && macGroupFilter) {
      // Mac model page: tag-based pills only for models in selected category
      const cat = MAC_CATEGORIES.find(c => c.label === macGroupFilter);
      const categoryModels = cat ? models.filter(m => cat.match(m.id)) : models;
      const tags = new Set<string>(['all']);
      categoryModels.forEach(model => {
        (model.filterTags || []).forEach((tag: string) => tags.add(tag));
      });
      return Array.from(tags).map(tag => ({
        id: tag,
        label: tag === 'all'
          ? t('allModels') || 'Alle modellen'
          : tag.charAt(0).toUpperCase() + tag.slice(1).replace(/([A-Z])/g, ' $1').trim(),
      }));
    }
    if (isMacPage && !macGroupFilter) {
      // Mac category page: no pills needed
      return [];
    }
    return VARIANT_FILTERS.filter(vf =>
      vf.id === 'all' || models.some(m => vf.match(m.id, m.filterTags))
    );
  }, [models, isMacPage, macGroupFilter, t]);

  // Client-side filtering
  const filteredModels = models.filter(model => {
    const tags = model.filterTags || [];
    // Mac category page: categories are rendered separately, no models needed
    if (isMacPage && !macGroupFilter) return false;
    // Mac model page: first filter by category, then by active tag filter
    if (isMacPage && macGroupFilter) {
      const cat = MAC_CATEGORIES.find(c => c.label === macGroupFilter);
      if (cat && !cat.match(model.id)) return false;
      return activeFilter === 'all' || tags.includes(activeFilter);
    }
    // All other device types: variant-based filter
    if (activeFilter === 'all') return true;
    const vf = VARIANT_FILTERS.find(v => v.id === activeFilter);
    return vf ? vf.match(model.id, model.filterTags) : true;
  });

  return (
    <Container>
      <MacTransitionWrapper $phase={isMacPage ? transitionPhase : 'idle'}>
      <ContentWrapper>
        <Header>
          <HeaderIconContainer>
            {getDeviceIcon(deviceType)}
          </HeaderIconContainer>
          <Title>
            {isMacPage && !macGroupFilter
              ? t('chooseYourMacType') || 'Welke Mac heeft u?'
              : isMacPage && macGroupFilter
              ? t('chooseYourModel').replace('{deviceType}', macGroupFilter)
              : t('chooseYourModel').replace('{deviceType}', deviceTypeName)}
          </Title>
          <Subtitle>
            {isMacPage && !macGroupFilter
              ? t('selectMacTypeSubtitle') || 'Selecteer het type Mac dat u wilt verkopen'
              : isMobile ? t('selectExactModelMobile') : t('selectExactModel')}
          </Subtitle>
        </Header>

        <FilterContainer>
          {onBack && (
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
              <BackButton onClick={isMacPage ? handleAnimatedBack : onBack}>
                <ArrowLeft size={16} />
                {t('back')}
              </BackButton>
            </div>
          )}
          {/* Hide search & filter pills on Mac category page */}
          {!(isMacPage && !macGroupFilter) && (
            <>
              <div style={{ marginBottom: '1.5rem' }}>
                <SearchWrapper>
                  <SearchInput
                    type="text"
                    placeholder={isMobile ? t('searchModelMobile') : t('searchModel')}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                  <SearchRight>
                    {!isLoading && <SearchCount>{debouncedSearch || activeFilter !== 'all' ? filteredModels.length : totalCount ?? filteredModels.length}</SearchCount>}
                    <SearchIcon>
                      {searchInput ? (
                        <button onClick={handleClearSearch}>✕</button>
                      ) : (
                        <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      )}
                    </SearchIcon>
                  </SearchRight>
                </SearchWrapper>
              </div>

              <FilterGrid>
                {filters.map(filter => (
                  <FilterPill
                    key={filter.id}
                    $isActive={activeFilter === filter.id}
                    onClick={() => setActiveFilter(filter.id)}
                  >
                    {filter.label}
                  </FilterPill>
                ))}
              </FilterGrid>
            </>
          )}
        </FilterContainer>

        {/* Mac Category Selection */}
        {isMacPage && !macGroupFilter && (
          <ModelGrid>
            {MAC_CATEGORIES
              .filter(cat => models.some(m => cat.match(m.id)))
              .map((cat, index) => {
                const firstModel = models.find(m => cat.match(m.id));
                const categoryImage = firstModel?.image || '/devices/imac.png';
                return (
                  <ModelCard key={cat.id} onClick={() => handleCategoryClick(cat.label)}>
                    <CardHeader>
                      <ModelImage
                        src={encodeURI(categoryImage)}
                        alt={cat.label}
                        className="high-quality-device-image"
                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                          e.currentTarget.src = '/devices/imac.png';
                        }}
                      />
                    </CardHeader>
                    <CardContent>
                      <ModelTitle>{cat.label}</ModelTitle>
                    </CardContent>
                    <CTAButton>
                      {t('viewModels') || 'Bekijk modellen'}
                      <Tag className="arrow-icon" size={16} />
                    </CTAButton>
                  </ModelCard>
                );
              })}
            <RequestCard $delay={MAC_CATEGORIES.length}>
              <RequestIcon>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 01-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 013.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 013.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 01-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
                </svg>
              </RequestIcon>
              <RequestTitle>{t('proposeDifferentModel')}</RequestTitle>
              <RequestDescription>{t('proposeDifferentModelDescription')}</RequestDescription>
              <RequestCTAButton onClick={() => setIsCustomRequestModalOpen(true)}>
                {t('proposeModelButton')}
              </RequestCTAButton>
            </RequestCard>
          </ModelGrid>
        )}

        {/* Loading State */}
        {!(isMacPage && !macGroupFilter) && isLoading && (
          <ModelGrid>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <SkeletonCard key={i}>
                <SkeletonHeader>
                  <SkeletonImage />
                </SkeletonHeader>
                <SkeletonContent>
                  <SkeletonTitle />
                  <SkeletonSubtitle />
                </SkeletonContent>
                <SkeletonButton />
              </SkeletonCard>
            ))}
          </ModelGrid>
        )}

        {/* Error State */}
        {!(isMacPage && !macGroupFilter) && !isLoading && error && (
          <ErrorContainer>
            <ErrorIcon>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            </ErrorIcon>
            <ErrorTitle>{t('errorLoadingModels')}</ErrorTitle>
            <ErrorMessage>{error}</ErrorMessage>
          </ErrorContainer>
        )}

        {/* Models Grid */}
        {!(isMacPage && !macGroupFilter) && !isLoading && !error && (
          <>
            <ModelGrid>
              {filteredModels.length === 0 && (debouncedSearch || activeFilter !== 'all') ? (
                <EmptyStateContainer>
                  <EmptyStateIcon>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.182 16.318A4.486 4.486 0 0012.016 15a4.486 4.486 0 00-3.198 1.318M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z" />
                    </svg>
                  </EmptyStateIcon>
                  <EmptyStateTitle>{t('noModelsFound')}</EmptyStateTitle>
                  <EmptyStateText>{t('tryDifferentSearch')}</EmptyStateText>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                    <button
                      onClick={() => { setSearchInput(''); setActiveFilter('all'); }}
                      style={{
                        padding: '0.6rem 1.25rem', borderRadius: '10px', fontSize: '0.85rem', fontWeight: 600,
                        background: '#0f172a', color: 'white', border: 'none', cursor: 'pointer',
                      }}
                    >
                      {t('clearSearch')}
                    </button>
                    <button
                      onClick={() => setIsCustomRequestModalOpen(true)}
                      style={{
                        padding: '0.6rem 1.25rem', borderRadius: '10px', fontSize: '0.85rem', fontWeight: 600,
                        background: 'white', color: '#0f172a', border: '1px solid #e2e8f0', cursor: 'pointer',
                      }}
                    >
                      {t('proposeModelButton')}
                    </button>
                  </div>
                </EmptyStateContainer>
              ) : filteredModels.length === 0 ? (
                <EmptyStateContainer>
                  <EmptyStateIcon>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                    </svg>
                  </EmptyStateIcon>
                  <EmptyStateTitle>{t('noModelsAvailable')}</EmptyStateTitle>
                  <EmptyStateText>{t('checkBackLater')}</EmptyStateText>
                </EmptyStateContainer>
              ) : (
                <>
                  {filteredModels.map((model, index) => {
                    // display_name shapes: "Name", "Name (2020)", "Name (M4, 2024)" and
                    // "Name (4th generation) (2020)". Title = everything before the last
                    // block; the block itself becomes the meta row ("M4 · 2024").
                    const { base: mainName, meta: rawMeta } = splitDeviceDisplayName(model.name);
                    const metaText = formatDeviceNameMeta(mainName, rawMeta) || undefined;
                    return (
                      <ModelCard key={model.id} onClick={() => onSelect(model.id, model.name)}>
                        <CardHeader>
                          <ModelImage
                            src={encodeURI(model.image)}
                            alt={model.name}
                            className="high-quality-device-image"
                            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                              const img = e.currentTarget;
                              if (img.getAttribute('data-fallback') !== '1') {
                                img.setAttribute('data-fallback', '1');
                                // Use device-type-specific generic fallback
                                const id = model.id.toLowerCase();
                                if (id.includes('ipad')) img.src = '/devices/ipad.png';
                                else if (id.includes('macbook')) img.src = '/devices/macbook.png';
                                else if (id.includes('imac')) img.src = '/devices/imac.png';
                                else if (id.includes('samsung') || id.includes('galaxy')) img.src = '/devices/samsung.png';
                                else if (id.includes('watch')) img.src = '/devices/apple-watch.png';
                                else img.src = '/devices/iphone.png';
                              }
                            }}
                          />
                        </CardHeader>
                        <CardContent>
                          <ModelTitle>
                            {mainName}
                            {metaText && <span className="model-meta">{metaText}</span>}
                          </ModelTitle>
                        </CardContent>
                        <CTAButton>
                          {deviceTypeName === 'Mac' && !macGroupFilter
                            ? t('viewModels') || 'Bekijk modellen'
                            : t('sellButton')}
                          <Tag className="arrow-icon" size={16} />
                        </CTAButton>
                      </ModelCard>
                    );
                  })}

                  <RequestCard $delay={filteredModels.length}>
                    <RequestIcon>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 01-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 013.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 013.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 01-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
                      </svg>
                    </RequestIcon>
                    <RequestTitle>{t('proposeDifferentModel')}</RequestTitle>
                    <RequestDescription>{t('proposeDifferentModelDescription')}</RequestDescription>
                    <RequestCTAButton onClick={() => setIsCustomRequestModalOpen(true)}>
                      {t('proposeModelButton')}
                    </RequestCTAButton>
                  </RequestCard>
                </>
              )}
            </ModelGrid>

            {/* Load More Button */}
            {hasMore && (
              <LoadMoreWrapper>
                <LoadMoreButton onClick={loadMore} disabled={isLoadingMore}>
                  {isLoadingMore ? (
                    <>
                      <SpinnerInline />
                      {t('loading')}
                    </>
                  ) : (
                    t('loadMore') || 'Show more'
                  )}
                </LoadMoreButton>
              </LoadMoreWrapper>
            )}
          </>
        )}
      </ContentWrapper>
      </MacTransitionWrapper>
      <CustomDeviceRequestModal
        isOpen={isCustomRequestModalOpen}
        onClose={() => setIsCustomRequestModalOpen(false)}
        deviceType={deviceTypeName}
      />
    </Container>
  );
};

export default DeviceModelList; 